import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Calendar, User, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

export type UnassignedAssignmentRow = {
  assignmentId: string;
  appointmentId: string;
  serviceId: string;
  serviceName: string;
  fromDateTime: string;
  appointmentDateTime: string;
  durationMinutes: number | null;
  price: number | string | null;
  appointmentStatus: string;
  customerFirstName: string;
  customerLastName: string;
  customerName: string;
};

type ProfessionalOption = {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
  canPerformServices?: boolean;
};

type Props = {
  businessId: string;
  professionals: ProfessionalOption[];
  /** Increment to force a reload of the unassigned list without remounting. */
  refreshKey?: number;
  onAssigned?: () => void;
};

const formatAppointmentDateTime = (iso: string | null | undefined): string => {
  if (!iso) return 'Date TBD';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return 'Date TBD';
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getProfessionalDisplayName = (professional: ProfessionalOption): string => {
  return (
    professional.name ||
    `${professional.firstName || professional.first_name || ''} ${professional.lastName || professional.last_name || ''}`.trim() ||
    'Professional'
  );
};

const UnassignedAssignmentsWarning: React.FC<Props> = ({
  businessId,
  professionals,
  refreshKey = 0,
  onAssigned
}) => {
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<UnassignedAssignmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const eligibleProfessionals = useMemo(
    () => (professionals || []).filter((p) => p.canPerformServices !== false),
    [professionals]
  );

  const loadUnassigned = async () => {
    if (!businessId) return;
    try {
      setLoading(true);
      const data = await apiService.getUnassignedAssignments(businessId);
      setCount(data?.count || 0);
      setRows(Array.isArray(data?.assignments) ? data.assignments : []);
    } catch (error) {
      console.error('Error loading unassigned assignments:', error);
      // Fail quietly so this banner never blocks the appointments page.
      setCount(0);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnassigned();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, refreshKey]);

  const openModal = async () => {
    try {
      setLoading(true);
      const data = await apiService.getUnassignedAssignments(businessId);
      const nextRows = Array.isArray(data?.assignments) ? data.assignments : [];
      setCount(data?.count || 0);
      setRows(nextRows);
      const nextSelections: Record<string, string> = {};
      nextRows.forEach((row) => {
        nextSelections[row.assignmentId] = '';
      });
      setSelections(nextSelections);
      if (nextRows.length === 0) {
        toast('No unassigned services found');
        return;
      }
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error opening unassigned assignments modal:', error);
      toast.error('Failed to load unassigned services');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const payloadAssignments = Object.entries(selections)
      .filter(([, professionalId]) => !!professionalId)
      .map(([assignmentId, professionalId]) => ({ assignmentId, professionalId }));

    if (payloadAssignments.length === 0) {
      toast.error('Select at least one professional to assign');
      return;
    }

    setIsSaving(true);
    try {
      const result = await apiService.assignProfessionalsToUnassigned({
        businessId,
        assignments: payloadAssignments
      });
      toast.success(
        result.updatedCount === 1
          ? '1 service assigned successfully'
          : `${result.updatedCount} services assigned successfully`
      );
      setIsModalOpen(false);
      await loadUnassigned();
      onAssigned?.();
    } catch (error: any) {
      console.error('Error assigning professionals:', error);
      toast.error(error?.response?.data?.message || 'Failed to save assignments');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && count === 0 && !isModalOpen) {
    return null;
  }

  if (count <= 0 && !isModalOpen) {
    return null;
  }

  return (
    <>
      {count > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  {count === 1
                    ? 'There is 1 unassignment found'
                    : `There are ${count} unassignments found`}
                </p>
                <p className="mt-0.5 text-sm text-amber-800">
                  Some upcoming services do not have a professional assigned. Please assign them manually so staff schedules stay accurate.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={openModal}
              disabled={loading || isSaving}
              className="inline-flex items-center justify-center rounded-lg bg-[#0B4778] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0a3d66] disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
            >
              Review & Assign
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assign Professionals</h2>
                <p className="mt-0.5 text-sm text-gray-500">
                  Choose a professional for each unassigned upcoming service.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                disabled={isSaving}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No unassigned services remain.
                </div>
              ) : (
                <div className="space-y-3">
                  {rows.map((row) => (
                    <div
                      key={row.assignmentId}
                      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-1.5">
                          <p className="text-sm font-semibold text-gray-900">{row.serviceName}</p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                            <span className="inline-flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 text-[#0B4778]" />
                              {formatAppointmentDateTime(row.fromDateTime || row.appointmentDateTime)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <User className="h-3.5 w-3.5 text-[#0B4778]" />
                              {row.customerName}
                            </span>
                          </div>
                          {row.durationMinutes != null && (
                            <p className="text-xs text-gray-500">
                              Duration: {row.durationMinutes} min
                              {row.price != null && row.price !== ''
                                ? ` • $${Number(row.price).toFixed(2)}`
                                : ''}
                            </p>
                          )}
                        </div>

                        <div className="w-full sm:w-64 flex-shrink-0">
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            Professional
                          </label>
                          <select
                            value={selections[row.assignmentId] || ''}
                            onChange={(e) =>
                              setSelections((prev) => ({
                                ...prev,
                                [row.assignmentId]: e.target.value
                              }))
                            }
                            disabled={isSaving}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#0B4778] focus:outline-none focus:ring-2 focus:ring-[#0B4778]"
                          >
                            <option value="">Select professional…</option>
                            {eligibleProfessionals.map((professional) => (
                              <option key={professional.id} value={professional.id}>
                                {getProfessionalDisplayName(professional)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                disabled={isSaving}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || rows.length === 0}
                className="inline-flex items-center justify-center rounded-lg bg-[#0B4778] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0a3d66] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  'Save Assignments'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnassignedAssignmentsWarning;
